import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, FileText, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function EditPlanogrammModal({ open, onClose, onSuccess, planogramm, bereiche, locations, token }) {
  const [regalName, setRegalName] = useState('');
  const [titel, setTitel] = useState('');
  const [bereichId, setBereichId] = useState('none');
  const [selectedStations, setSelectedStations] = useState([]);
  const [gueltigAb, setGueltigAb] = useState('');
  const [saving, setSaving] = useState(false);

  // PDF-Ersetzen State
  const [newPdfFile, setNewPdfFile] = useState(null);
  const [replacingPdf, setReplacingPdf] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (planogramm) {
      setRegalName(planogramm.regal_name || '');
      setTitel(planogramm.titel || '');
      setBereichId(planogramm.bereich_id || 'none');
      setSelectedStations(planogramm.station_ids || []);
      setGueltigAb(planogramm.gueltig_ab ? planogramm.gueltig_ab.split('T')[0] : '');
      setNewPdfFile(null);
    }
  }, [planogramm]);

  const toggleStation = (id) => {
    setSelectedStations(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const selectAllStations = () => {
    if (selectedStations.length === locations.length) {
      setSelectedStations([]);
    } else {
      setSelectedStations(locations.map(l => l.id));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Nur PDF-Dateien erlaubt');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('PDF zu groß (max. 50 MB)');
      return;
    }
    setNewPdfFile(file);
  };

  const handleSave = async () => {
    if (!regalName.trim()) { toast.error('Regalname ist Pflicht'); return; }
    if (!titel.trim()) { toast.error('Titel ist Pflicht'); return; }
    if (selectedStations.length === 0) { toast.error('Mindestens eine Station'); return; }

    setSaving(true);
    try {
      // 1. Metadaten speichern
      await axios.patch(`${API}/api/planogramm/${planogramm.id}`, {
        regal_name: regalName.trim(),
        titel: titel.trim(),
        bereich_id: bereichId === 'none' ? '' : bereichId,
        station_ids: selectedStations,
        gueltig_ab: gueltigAb || '',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 2. Falls neue PDF-Datei gewählt → auch ersetzen
      if (newPdfFile) {
        setReplacingPdf(true);
        const formData = new FormData();
        formData.append('pdf_file', newPdfFile);
        await axios.post(`${API}/api/planogramm/${planogramm.id}/replace-pdf`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      toast.success(newPdfFile ? 'Planogramm und PDF aktualisiert' : 'Planogramm aktualisiert');
      setNewPdfFile(null);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
      setReplacingPdf(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-xl max-h-[90dvh] overflow-y-auto" data-testid="edit-planogramm-modal" aria-describedby="edit-planogramm-description">
        <DialogHeader>
          <DialogTitle>Planogramm bearbeiten</DialogTitle>
          <p id="edit-planogramm-description" className="text-sm text-muted-foreground">Metadaten und PDF anpassen</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="edit_regal_name">Regalname *</Label>
            <Input
              id="edit_regal_name"
              value={regalName}
              onChange={(e) => setRegalName(e.target.value)}
              data-testid="edit-input-regal-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit_titel">Titel *</Label>
            <Input
              id="edit_titel"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              data-testid="edit-input-titel"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Bereich</Label>
            <Select value={bereichId} onValueChange={setBereichId}>
              <SelectTrigger data-testid="edit-select-bereich">
                <SelectValue placeholder="Kein Bereich" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Kein Bereich</SelectItem>
                {bereiche.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Stationen *</Label>
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={selectAllStations}>
                {selectedStations.length === locations.length ? 'Keine' : 'Alle'} auswaehlen
              </Button>
            </div>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2" data-testid="edit-station-list">
              {locations.map(loc => (
                <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedStations.includes(loc.id)}
                    onCheckedChange={() => toggleStation(loc.id)}
                  />
                  <span className="text-sm">{loc.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit_gueltig_ab">Gueltig ab</Label>
            <Input
              id="edit_gueltig_ab"
              type="date"
              value={gueltigAb}
              onChange={(e) => setGueltigAb(e.target.value)}
              data-testid="edit-input-gueltig-ab"
            />
          </div>

          {/* PDF ersetzen */}
          <div className="space-y-1.5">
            <Label>PDF ersetzen <span className="text-muted-foreground font-normal">(optional)</span></Label>
            {newPdfFile ? (
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <FileText className="h-4 w-4 text-green-600 shrink-0" />
                <span className="text-sm text-green-800 dark:text-green-300 truncate flex-1">
                  {newPdfFile.name}
                </span>
                <span className="text-xs text-green-600 shrink-0">
                  ({(newPdfFile.size / 1024 / 1024).toFixed(1)} MB)
                </span>
                <button
                  type="button"
                  onClick={() => { setNewPdfFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="p-0.5 hover:bg-green-200 rounded transition-colors shrink-0"
                >
                  <X className="h-3.5 w-3.5 text-green-700" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
                data-testid="replace-pdf-btn"
              >
                <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground">Neues PDF auswählen (max. 50 MB)</span>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="pdf-file-input"
            />
            <p className="text-xs text-muted-foreground">
              Das aktuelle PDF wird durch das neue ersetzt. Thumbnail wird automatisch neu generiert.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="cancel-edit-btn" className="w-full sm:w-auto">
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-edit-btn" className="gap-2 w-full sm:w-auto">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving
              ? (replacingPdf ? 'PDF wird ersetzt...' : 'Wird gespeichert...')
              : (newPdfFile ? 'Speichern + PDF ersetzen' : 'Speichern')
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
